import React, { useReducer, useCallback, useContext } from 'react';
import api from '../../utils/api';
import MatchGroupContext from './matchGroupContext';
import matchGroupReducer from './matchGroupReducer';
import AuthContext from '../auth/authContext';
import {
  GET_MATCH_GROUPS,
  GET_MATCH_GROUP,
  ADD_MATCH_GROUP,
  UPDATE_MATCH_GROUP,
  DELETE_MATCH_GROUP,
  ADD_MATCHES_TO_GROUP,
  REMOVE_MATCHES_FROM_GROUP,
  MATCH_GROUP_ERROR,
  CLEAR_MATCH_GROUPS,
  SET_LOADING
} from '../types';

const MatchGroupState = (props) => {
  const initialState = {
    matchGroups: [],
    currentGroup: null,
    loading: true,
    error: null
  };

  const [state, dispatch] = useReducer(matchGroupReducer, initialState);
  const { user } = useContext(AuthContext);

  // Set loading
  const setLoading = useCallback(() => {
    dispatch({ type: SET_LOADING });
  }, [dispatch]);

  // Get all match groups
  const getMatchGroups = useCallback(async () => {
    // Only allow authenticated users to fetch match groups
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: GET_MATCH_GROUPS,
        payload: []
      });
      return;
    }

    try {
      setLoading();
      const res = await api.get('/api/matchgroups');
      
      dispatch({
        type: GET_MATCH_GROUPS,
        payload: res.data
      });
    } catch (err) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: err.response?.data?.msg || 'Error fetching match groups'
      });
    }
  }, [dispatch, setLoading, user]);

  // Get a single match group by ID
  const getMatchGroupById = useCallback(async (id) => {
    // Only allow authenticated users to fetch match groups
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: 'You must be logged in to view match groups'
      });
      return;
    }

    try {
      setLoading();
      const res = await api.get(`/api/matchgroups/${id}`);
      
      dispatch({
        type: GET_MATCH_GROUP,
        payload: res.data
      });
    } catch (err) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: err.response?.data?.msg || 'Error fetching match group'
      });
    }
  }, [dispatch, setLoading, user]);

  // Create a new match group
  const createMatchGroup = useCallback(async (groupData) => {
    // Only allow authenticated users to create match groups
    if (!user || !localStorage.getItem('token')) {
      const error = 'You must be logged in to create match groups';
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: error
      });
      return { success: false, error };
    }

    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      setLoading();
      const res = await api.post('/api/matchgroups', groupData, config);
      
      dispatch({
        type: ADD_MATCH_GROUP,
        payload: res.data
      });
      
      // Refresh match groups after creating
      setTimeout(() => {
        getMatchGroups();
      }, 100);
      
      return { success: true, data: res.data };
    } catch (err) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: err.response?.data?.msg || 'Error creating match group'
      });
      
      return { success: false, error: err.response?.data?.msg || 'Error creating match group' };
    }
  }, [dispatch, setLoading, user]);

  // Update a match group
  const updateMatchGroup = useCallback(async (groupData) => {
    // Only allow authenticated users to update match groups
    if (!user || !localStorage.getItem('token')) {
      const error = 'You must be logged in to update match groups';
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: error
      });
      return { success: false, error };
    }

    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      setLoading();
      const res = await api.put(`/api/matchgroups/${groupData._id}`, groupData, config);
      
      dispatch({
        type: UPDATE_MATCH_GROUP,
        payload: res.data
      });
      
      // Refresh match groups after updating
      setTimeout(() => {
        getMatchGroups();
      }, 100);
      
      return { success: true, data: res.data };
    } catch (err) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: err.response?.data?.msg || 'Error updating match group'
      });
      
      return { success: false, error: err.response?.data?.msg || 'Error updating match group' };
    }
  }, [dispatch, setLoading, user]);

  // Delete a match group
  const deleteMatchGroup = useCallback(async (id) => {
    // Only allow authenticated users to delete match groups
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: 'You must be logged in to delete match groups'
      });
      return;
    }

    try {
      setLoading();
      await api.delete(`/api/matchgroups/${id}`);
      
      dispatch({
        type: DELETE_MATCH_GROUP,
        payload: id
      });
      
      // Refresh match groups after deletion
      setTimeout(() => {
        getMatchGroups();
      }, 100);
    } catch (err) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: err.response?.data?.msg || 'Error deleting match group'
      });
    }
  }, [dispatch, setLoading, user]);

  // Add matches to a group
  const addMatchesToGroup = useCallback(async (groupId, matchData) => {
    // Only allow authenticated users to add matches to groups
    if (!user || !localStorage.getItem('token')) {
      const error = 'You must be logged in to add matches to groups';
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: error
      });
      return { success: false, error };
    }

    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      setLoading();
      const res = await api.put(`/api/matchgroups/${groupId}/matches`, matchData, config);
      
      dispatch({
        type: ADD_MATCHES_TO_GROUP,
        payload: res.data
      });
      
      // Refresh current group if viewing it
      if (state.currentGroup && state.currentGroup._id === groupId) {
        setTimeout(() => {
          getMatchGroupById(groupId);
        }, 100);
      }
      
      return { success: true, data: res.data };
    } catch (err) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: err.response?.data?.msg || 'Error adding matches to group'
      });
      
      return { success: false, error: err.response?.data?.msg || 'Error adding matches to group' };
    }
  }, [dispatch, setLoading, user]);

  // Remove matches from a group
  const removeMatchesFromGroup = useCallback(async (groupId, matchData) => {
    // Only allow authenticated users to remove matches from groups
    if (!user || !localStorage.getItem('token')) {
      const error = 'You must be logged in to remove matches from groups';
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: error
      });
      return { success: false, error };
    }

    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      setLoading();
      const res = await api.put(`/api/matchgroups/${groupId}/matches/remove`, matchData, config);
      
      dispatch({
        type: REMOVE_MATCHES_FROM_GROUP,
        payload: res.data
      });
      
      // Refresh current group if viewing it
      if (state.currentGroup && state.currentGroup._id === groupId) {
        setTimeout(() => {
          getMatchGroupById(groupId);
        }, 100);
      }
      
      return { success: true, data: res.data };
    } catch (err) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: err.response?.data?.msg || 'Error removing matches from group'
      });
      
      return { success: false, error: err.response?.data?.msg || 'Error removing matches from group' };
    }
  }, [dispatch, setLoading, user]);

  // Invite friend to group
  const inviteToGroup = useCallback(async (groupId, friendId, permission = 'edit') => {
    // Only allow authenticated users to invite friends to groups
    if (!user || !localStorage.getItem('token')) {
      const error = 'You must be logged in to invite friends to groups';
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: error
      });
      return { success: false, error };
    }

    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      setLoading();
      const res = await api.put(`/api/matchgroups/${groupId}/invite`, { friendId, permission }, config);
      
      dispatch({
        type: UPDATE_MATCH_GROUP,
        payload: res.data
      });
      
      return { success: true, data: res.data };
    } catch (err) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: err.response?.data?.msg || 'Error inviting friend to group'
      });
      
      return { success: false, error: err.response?.data?.msg || 'Error inviting friend to group' };
    }
  }, [dispatch, setLoading, user]);

  // Respond to group invitation
  const respondToInvitation = useCallback(async (groupId, response) => {
    // Only allow authenticated users to respond to invitations
    if (!user || !localStorage.getItem('token')) {
      const error = 'You must be logged in to respond to invitations';
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: error
      });
      return { success: false, error };
    }

    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      setLoading();
      await api.put(`/api/matchgroups/${groupId}/respond`, { response }, config);
      
      // Refresh match groups after responding to invitation
      await getMatchGroups();
      
      return { success: true };
    } catch (err) {
      dispatch({
        type: MATCH_GROUP_ERROR,
        payload: err.response?.data?.msg || 'Error responding to invitation'
      });
      
      return { success: false, error: err.response?.data?.msg || 'Error responding to invitation' };
    }
  }, [dispatch, setLoading, getMatchGroups, user]);

  // Get pending invitations
  const getPendingInvitations = useCallback(async () => {
    // Only allow authenticated users to fetch pending invitations
    if (!user || !localStorage.getItem('token')) {
      return { success: true, data: [] };
    }

    try {
      const res = await api.get('/api/matchgroups/invitations');
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.msg || 'Error fetching invitations' };
    }
  }, [user]);

  // Clear match groups
  const clearMatchGroups = useCallback(() => {
    dispatch({ type: CLEAR_MATCH_GROUPS });
  }, [dispatch]);

  return (
    <MatchGroupContext.Provider
      value={{
        matchGroups: state.matchGroups,
        currentGroup: state.currentGroup,
        loading: state.loading,
        error: state.error,
        getMatchGroups,
        getMatchGroupById,
        createMatchGroup,
        updateMatchGroup,
        deleteMatchGroup,
        addMatchesToGroup,
        removeMatchesFromGroup,
        inviteToGroup,
        respondToInvitation,
        getPendingInvitations,
        clearMatchGroups
      }}
    >
      {props.children}
    </MatchGroupContext.Provider>
  );
};

export default MatchGroupState;